import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as React from "react";
import { ProseDocument } from "components/elements";
import { FileAttachment } from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { trpc } from "lib/trpc";
import { Poll } from "screens/social/Poll";
import { SocialPostEditor } from "screens/social/SocialPostEditor";
import { SocialPostHeader } from "screens/social/post/SocialPostHeader";
import { SocialPostMeta } from "screens/social/post/SocialPostMeta";
import { ClientCurrentUser, ClientFileEntry, ClientPost, PostUpdate } from "@compass/shared/types";
import { PostModal } from "./PostModal";
import { MetaPreview } from "./PreviewLink";

type SocialPostProps = {
    post: ClientPost;
    handleDeletePost: () => void;
    handleUpdatePost: (params: PostUpdate, callback: () => void) => void;
    isLoading?: boolean;
    currentUser?: ClientCurrentUser;
    isModalView?: boolean;
    compact?: boolean;
    target?: string;
};

export const SocialPost = (props: SocialPostProps) => {
    const {
        post,
        currentUser,
        isLoading,
        handleDeletePost,
        isModalView = false,
        compact = false,
        target,
    } = props;
    const [isPostEditOpen, setIsPostEditOpen] = useState<boolean>(false);
    const [selectedPost, setSelectedPost] = useState<ClientPost | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [vote, setVote] = useState<number | null>(null);
    const [isVoted, setIsVoted] = useState<boolean>(post.poll?.view === "results" ? true : false);
    const [hasAlreadyVoted, setHasAlreadyVoted] = useState<boolean>(
        post.poll?.view === "results" ? true : false,
    );

    const { enqueueSnackbar } = useSnackbar();
    const queryClient = trpc.useContext();
    const rawQueryClient = useQueryClient();
    const updatePostMutate = trpc.social.posts.update.useMutation();
    const pollVoteMutate = trpc.social.posts.vote.useMutation();

    const handleUpdatePost = (params: PostUpdate, callback: () => void) => {
        updatePostMutate.mutate(params, {
            onSuccess: (updatedPost) => {
                callback();
                setIsPostEditOpen(false);
                queryClient.social.posts.latest.invalidate();
                queryClient.social.posts.get.invalidate({ postId: props.post.id });
                const postId = props.post.id;
                rawQueryClient.setQueriesData(
                    { queryKey: [["feed", "unified"]] },
                    (old: unknown) => {
                        if (!old || typeof old !== "object") return old;
                        const data = old as {
                            pages: Array<{
                                items: { type: string; post?: { id: number }; sortDate: string }[];
                                nextCursor: string | null;
                            }>;
                            pageParams: unknown[];
                        };
                        return {
                            ...data,
                            pages: data.pages.map((page) => ({
                                ...page,
                                items: page.items.map((item) =>
                                    item.type === "newPost" && item.post?.id === postId
                                        ? { ...item, post: updatedPost }
                                        : item,
                                ),
                            })),
                        };
                    },
                );
                enqueueSnackbar("Post updated successfully.", { variant: "success" });
            },
            onError: (e) => {
                console.error(e);
                enqueueSnackbar(`${e}`, { variant: "error" });
            },
        });
    };

    const handleVote = (optionId: number) => {
        const postId = post.id;
        pollVoteMutate.mutate(
            {
                postId,
                optionId,
            },
            {
                onSuccess: (poll) => {
                    // Patch every cached shape that renders this post so the
                    // new percents + myVote show up without a refetch.
                    queryClient.social.posts.get.setData({ postId }, (old) =>
                        old ? { ...old, poll } : old,
                    );
                    rawQueryClient.setQueriesData(
                        { queryKey: [["social", "posts", "latest"]] },
                        (old: unknown) => {
                            if (!old || typeof old !== "object") return old;
                            const data = old as {
                                pages: Array<{ posts: Array<{ id: number }> }>;
                                pageParams: unknown[];
                            };
                            return {
                                ...data,
                                pages: data.pages.map((page) => ({
                                    ...page,
                                    posts: page.posts.map((p) =>
                                        p.id === postId ? { ...p, poll } : p,
                                    ),
                                })),
                            };
                        },
                    );
                    rawQueryClient.setQueriesData(
                        { queryKey: [["feed", "unified"]] },
                        (old: unknown) => {
                            if (!old || typeof old !== "object") return old;
                            const data = old as {
                                pages: Array<{
                                    items: Array<{ type: string; post?: { id: number } }>;
                                }>;
                                pageParams: unknown[];
                            };
                            return {
                                ...data,
                                pages: data.pages.map((page) => ({
                                    ...page,
                                    items: page.items.map((item) => {
                                        if (item.type !== "newPost" || item.post?.id !== postId)
                                            return item;
                                        return {
                                            ...item,
                                            post: { ...(item.post as object), poll },
                                        };
                                    }),
                                })),
                            };
                        },
                    );
                    setIsVoted(true);
                    setHasAlreadyVoted(true);
                },
            },
        );
    };

    const openPostModal = (post: ClientPost) => {
        setSelectedPost(post);
        setIsModalOpen(true);
    };

    const previews = post.links ?? [];

    return (
        <>
            <SocialPostHeader
                post={post}
                canEdit={post.userCanEdit}
                setIsPostEditOpen={setIsPostEditOpen}
                handleDeletePost={handleDeletePost}
                compact={compact}
            />
            <div className={compact ? "h-1" : "h-2"} />

            <div className="text-normal flex flex-col gap-1 text-[#424242]">
                <div className="prose prose-sm list-inside list-disc [&_li]:ml-5 [&_ol]:list-decimal [&_ul]:list-disc">
                    {compact ? (
                        <Link
                            to="/social/post/$id"
                            params={{ id: post.id.toString() }}
                            target={target}
                            className="line-clamp-2 block py-0.5 text-sm font-semibold text-[#2DA7E0] no-underline hover:underline"
                        >
                            {post.title}
                        </Link>
                    ) : (
                        <Link
                            to="/social/post/$id"
                            params={{ id: post.id.toString() }}
                            target={target}
                            className="block py-0.5 text-lg font-medium text-[#424242] no-underline hover:underline"
                        >
                            {post.title}
                        </Link>
                    )}
                    <div className={compact ? "line-clamp-4 overflow-hidden" : ""}>
                        <ProseDocument doc={post.contentJson} />
                    </div>
                </div>

                {!compact && (
                    <Poll
                        options={post?.poll?.pollOptions || []}
                        handleVote={handleVote}
                        vote={vote}
                        setVote={setVote}
                        hasAlreadyVoted={hasAlreadyVoted}
                        setIsVoted={setIsVoted}
                        isPollIncluded={post.poll?.view === "poll" || post.poll?.view === "results"}
                        totalVotes={post?.poll?.totalVotes}
                    />
                )}
                {compact && post.poll && (
                    <span className="mt-1 inline-block rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                        Poll
                    </span>
                )}
                {post.images && post.images.length > 0 && (
                    <div className="mt-2 flex flex-col gap-2">
                        {(compact ? post.images.slice(0, 1) : post.images).map(
                            (file: ClientFileEntry) => (
                                <FileAttachment key={file.id} file={file} />
                            ),
                        )}
                        {compact && post.images.length > 1 && (
                            <span className="text-xs text-gray-400">
                                +{post.images.length - 1} more
                            </span>
                        )}
                    </div>
                )}
            </div>

            {!compact && previews.length > 0 && (
                <div className="flex flex-col gap-2">
                    {previews.map((preview) => (
                        <MetaPreview preview={preview} key={`PREVIEW_${preview.id}`} />
                    ))}
                </div>
            )}

            <div className={compact ? "h-2" : "h-4"} />

            <div className="my-1 flex justify-between border-b-[1px] border-t-[1px] border-[#d4d5d5] py-2">
                <div className="flex items-center gap-1">
                    {!compact && <SocialPostMeta {...props} />}
                    <FavoriteButton
                        target={{ type: "post", id: post.id }}
                        size={compact ? 14 : 16}
                    />
                </div>

                {post?.comments?.length > 0 && (
                    <div
                        className={`text-sm text-gray-500 ${!isModalView && !compact ? "cursor-pointer hover:underline" : ""}`}
                        onClick={() => {
                            if (!isModalView && !compact) {
                                openPostModal(post);
                            }
                        }}
                    >
                        {post.comments.length} comment{post.comments.length > 1 ? "s" : ""}
                    </div>
                )}
            </div>

            {!compact && (
                <>
                    <div className="h-4" />
                    <SocialPostEditor
                        currentUser={currentUser}
                        isLoading={isLoading}
                        handleEditPost={handleUpdatePost}
                        isEdit
                        setIsOpen={setIsPostEditOpen}
                        isOpen={isPostEditOpen}
                        post={post}
                    />
                </>
            )}

            {selectedPost && !compact && (
                <PostModal isOpen={isModalOpen} onClose={setIsModalOpen} post={selectedPost} />
            )}
        </>
    );
};
